/**
 * Gestionnaire d'erreurs centralisé
 * Fournit des messages d'erreur sécurisés pour l'utilisateur
 */

/**
 * Obtient un message d'erreur sécurisé pour l'utilisateur
 * Ne révèle pas d'informations sensibles sur l'infrastructure
 */
export const getErrorMessage = (error) => {
  // Si c'est une erreur réseau
  if (!error.response) {
    return {
      title: 'Erreur de connexion',
      description: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
      type: 'network',
    };
  }

  const { status, data } = error.response;

  // Messages d'erreur selon le code HTTP
  switch (status) {
    case 400:
      return {
        title: 'Données invalides',
        description: data?.message || 'Les données envoyées sont invalides. Veuillez vérifier vos informations.',
        type: 'validation',
      };

    case 401:
      return {
        title: 'Session expirée',
        description: 'Votre session a expiré. Veuillez vous reconnecter.',
        type: 'auth',
      };

    case 403:
      return {
        title: 'Accès interdit',
        description: 'Vous n\'avez pas les permissions nécessaires pour effectuer cette action.',
        type: 'permission',
      };

    case 404:
      return {
        title: 'Ressource non trouvée',
        description: 'La ressource demandée n\'existe pas ou a été supprimée.',
        type: 'notFound',
      };

    case 409:
      return {
        title: 'Conflit',
        description: data?.message || 'Cette ressource existe déjà ou est en conflit avec une autre.',
        type: 'conflict',
      };

    case 422:
      return {
        title: 'Erreur de validation',
        description: data?.message || 'Les données fournies ne respectent pas les règles de validation.',
        type: 'validation',
      };

    case 500:
      return {
        title: 'Erreur serveur',
        description: 'Une erreur est survenue sur le serveur. Veuillez réessayer plus tard.',
        type: 'server',
      };

    case 503:
      return {
        title: 'Service indisponible',
        description: 'Le service est temporairement indisponible. Veuillez réessayer plus tard.',
        type: 'service',
      };

    default:
      return {
        title: 'Erreur',
        description: data?.message || 'Une erreur inattendue est survenue. Veuillez réessayer.',
        type: 'unknown',
      };
  }
};

/**
 * Log l'erreur de manière sécurisée (pour le debugging)
 * Ne log pas d'informations sensibles
 */
export const logError = (error, context = '') => {
  const errorInfo = {
    context,
    message: error.message,
    status: error.response?.status,
    url: error.config?.url,
    timestamp: new Date().toISOString(),
  };

  // En production, envoyer à un service de logging (Sentry, LogRocket, etc.)
  if (process.env.NODE_ENV === 'production') {
    // TODO: Intégrer un service de logging
    // Sentry.captureException(error, { extra: errorInfo });
  } else {
    // En développement, logger dans la console
    console.error('Erreur:', errorInfo);
  }
};

/**
 * Formate l'erreur pour l'affichage dans un toast Chakra UI
 */
export const formatErrorForToast = (error) => {
  const errorMessage = getErrorMessage(error);
  logError(error, errorMessage.type);

  return {
    title: errorMessage.title,
    description: errorMessage.description,
    status: 'error',
    duration: 5000,
    isClosable: true,
  };
};

